import React from 'react';
import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import Text from 'components/text';
import { AdminCcMatch, CcMatchOrderBy } from 'hooks/swrRequests/useAdminCcMatches';
import { formatShortDate } from 'utils/helpers';

import type { SortDirection } from './types';
import { FONT } from './constants';
import { getInitials } from './get-initials';
import Badge from './components/badge';
import ArCell from './components/ar-cell';
import TableState from './components/table-state';
import { MiniSortPill, PlainHeaderCell, SortableHeaderCell } from './components/table-header';

const MatchRow: React.FC<{ match: AdminCcMatch }> = ({ match }) => {
  const { artist1: a1, artist2: a2 } = match;
  return (
    <TableRow hover>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            src={a1.image ?? undefined}
            sx={{ width: 32, height: 32, fontSize: 11, flexShrink: 0 }}
          >
            {getInitials(a1.name)}
          </Avatar>
          <Box component="span" color="#94a3b8" fontSize={14} flexShrink={0}>
            ×
          </Box>
          <Avatar
            src={a2.image ?? undefined}
            sx={{ width: 32, height: 32, fontSize: 11, flexShrink: 0 }}
          >
            {getInitials(a2.name)}
          </Avatar>
          <Text
            component="span"
            fontSize={FONT.lg}
            fontWeight={600}
            marginLeft="6px"
            flex={1}
            minWidth={0}
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {a1.name} × {a2.name}
          </Text>
        </Box>
      </TableCell>
      <TableCell>
        <Badge>{match.method === 'auto' ? 'Auto-matched' : 'Team-matched'}</Badge>
      </TableCell>
      <TableCell>
        <ArCell user={match.arUser1} />
      </TableCell>
      <TableCell>
        <ArCell user={match.arUser2} />
      </TableCell>
      <TableCell>
        <ArCell user={match.creator} />
      </TableCell>
      <TableCell sx={{ color: '#0f172a', fontSize: FONT.base }}>
        {formatShortDate(match.matchDate ?? match.createdAt)}
      </TableCell>
    </TableRow>
  );
};

interface MatchesTableProps {
  matches: AdminCcMatch[];
  isLoading: boolean;
  error: unknown;
  search: string;
  orderBy: CcMatchOrderBy;
  orderDirection: SortDirection;
  onSort: (column: CcMatchOrderBy) => void;
  onClearSearch: () => void;
}

const MatchesTable: React.FC<MatchesTableProps> = ({
  matches,
  isLoading,
  error,
  search,
  orderBy,
  orderDirection,
  onSort,
  onClearSearch,
}) => {
  if (isLoading || error || matches.length === 0) {
    return (
      <TableState
        isLoading={isLoading}
        error={error}
        isEmpty={matches.length === 0}
        search={search}
        onClearSearch={onClearSearch}
        emptyMessage={search ? `No matches match "${search}"` : 'No matches yet.'}
        errorMessage="Couldn't load matches. Please try again."
      />
    );
  }

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', minWidth: 820 }}>
        {/* Proportional column widths: Match | How | A&R · 1st | A&R · 2nd | Creator | Date */}
        <colgroup>
          <col style={{ width: '30%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: FONT.base, fontWeight: 500, color: '#64748b' }}>
              <Box
                component="span"
                display="inline-flex"
                alignItems="center"
                flexWrap="wrap"
                gap="8px"
              >
                <Box
                  component="span"
                  fontSize={FONT.sm}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  Match
                </Box>
                <MiniSortPill
                  label="1st"
                  active={orderBy === 'artist1'}
                  direction={orderDirection}
                  onClick={() => onSort('artist1')}
                />
                <MiniSortPill
                  label="2nd"
                  active={orderBy === 'artist2'}
                  direction={orderDirection}
                  onClick={() => onSort('artist2')}
                />
              </Box>
            </TableCell>
            <SortableHeaderCell
              label="How"
              active={orderBy === 'method'}
              direction={orderDirection}
              onClick={() => onSort('method')}
            />
            <PlainHeaderCell label="A&R 1" />
            <PlainHeaderCell label="A&R 2" />
            <SortableHeaderCell
              label="Creator"
              active={orderBy === 'creator'}
              direction={orderDirection}
              onClick={() => onSort('creator')}
            />
            <SortableHeaderCell
              label="Date"
              active={orderBy === 'matchDate'}
              direction={orderDirection}
              onClick={() => onSort('matchDate')}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.map((match) => (
            <MatchRow key={match.id} match={match} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MatchesTable;
